import React, { useState, useCallback, useRef, useEffect } from 'react';
import './index.css';
import { T } from './data/translations';
import { INDUSTRIES } from './data/industries';
import IndustrySelector from './components/IndustrySelector';
import AppShell from './components/AppShell';

const VALID_PAGES = ['dashboard', 'conversations', 'crm', 'marketing', 'support', 'interactive'];

function parseHash() {
  const raw = (window.location.hash || '').replace(/^#\/?/, '');
  if (!raw) return { industryId: null, page: 'dashboard' };
  const [industryId, page] = raw.split('/');
  return {
    industryId: INDUSTRIES[industryId] ? industryId : null,
    page: VALID_PAGES.includes(page) ? page : 'dashboard',
  };
}

function writeHash(industryId, page) {
  const next = industryId ? `#/${industryId}/${page || 'dashboard'}` : '#/';
  if (window.location.hash !== next) {
    window.history.replaceState(null, '', next);
  }
}

export default function App() {
  const initial = parseHash();
  const [lang, setLang] = useState('ar');
  const [industry, setIndustry] = useState(initial.industryId ? INDUSTRIES[initial.industryId] : null);
  const [page, setPage] = useState(initial.page);
  const [demoPhase, setDemoPhase] = useState(0);
  const [demoPhone, setDemoPhone] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const notifId = useRef(0);

  const t = T[lang];
  const dir = lang === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    writeHash(industry?.id, page);
  }, [industry, page]);

  useEffect(() => {
    const onHashChange = () => {
      const { industryId, page: newPage } = parseHash();
      setIndustry(industryId ? INDUSTRIES[industryId] : null);
      setPage(newPage);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const notify = useCallback((message) => {
    const id = ++notifId.current;
    setNotifications(prev => [...prev, { id, message }]);
    setTimeout(() => setNotifications(prev => prev.filter(n => n.id !== id)), 4000);
  }, []);

  const handleSelectIndustry = useCallback((id) => {
    setIndustry(INDUSTRIES[id]);
    setPage('dashboard');
    setDemoPhase(0);
  }, []);

  const handleChangeIndustry = useCallback(() => {
    setIndustry(null);
    setPage('dashboard');
    setDemoPhase(0);
  }, []);

  if (!industry) {
    return (
      <div dir={dir}>
        <IndustrySelector lang={lang} t={t} onSelect={handleSelectIndustry} onToggleLang={() => setLang(l => l === 'ar' ? 'en' : 'ar')} />
      </div>
    );
  }

  return (
    <div dir={dir}>
      <AppShell
        lang={lang}
        t={t}
        industry={industry}
        page={page}
        setPage={setPage}
        demoPhase={demoPhase}
        setDemoPhase={setDemoPhase}
        demoPhone={demoPhone}
        setDemoPhone={setDemoPhone}
        onToggleLang={() => setLang(l => l === 'ar' ? 'en' : 'ar')}
        onChangeIndustry={handleChangeIndustry}
        notifications={notifications}
        notify={notify}
      />
    </div>
  );
}
